import {
  CONTAINER_TYPE_OPTIONS,
  type Country,
  CURRENCY_OPTIONS,
  Currency,
  DEFAULT_CONTAINER_TYPE,
  isFieldErrorResponse,
  type PointToPointTransportCostBreakdownResultDTO,
  PortTypes,
  useApiTransportPricesV1EstimateCreate,
} from 'api';
import { formatDate } from 'date';
import {
  AddressAutocomplete,
  Form,
  IncotermSelectField,
  type InferType,
  PortAutocomplete,
  SelectField,
  TextField,
  useForm,
  yupResolver,
} from 'form';
import { type I18nTranslateFunction, useTranslation } from 'i18n';
import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  formatCurrency,
  Grid,
  Page,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from 'ui';

import { displayLocalCostCategory, displayMissingCost, displayTransportCostSource } from '../../utils';
import { createTransportCostsEstimateSchema } from './schema';

type FormValues = InferType<typeof createTransportCostsEstimateSchema>;

const DEFAULT_VALUES: Partial<FormValues> = {
  container_type: DEFAULT_CONTAINER_TYPE,
  currency: Currency.GBP,
  demand_incoterm: 'DDP',
  nearest_city_max_radius: 25,
  supply_incoterm: 'EXW',
};

const getIncludedDisplayedName = (isIncluded: boolean, t: I18nTranslateFunction<string>) =>
  isIncluded ? t('transportCostsEstimator.values.yes') : t('transportCostsEstimator.values.no');

const displayCostInTargetAndOriginalCurrency = (
  originalCost: string,
  originalCurrency: Currency,
  targetCost: string,
  targetCurrency: Currency
) => {
  if (originalCurrency === targetCurrency) {
    return formatCurrency(originalCost, { currencyCode: originalCurrency });
  }
  return `${formatCurrency(targetCost, { currencyCode: targetCurrency })} (${formatCurrency(originalCost, { currencyCode: originalCurrency })})`;
};

interface SeparatorRowProps {
  title: string;
}

const SeparatorRow = ({ title }: SeparatorRowProps) => (
  <TableRow sx={{ bgcolor: 'lightgrey' }}>
    <TableCell>
      <Typography fontWeight="bold">{title}</Typography>
    </TableCell>
    <TableCell />
    <TableCell />
  </TableRow>
);

export const TransportCostsEstimator = () => {
  const { t } = useTranslation('orders');
  const form = useForm<FormValues, unknown, FormValues>({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(createTransportCostsEstimateSchema),
  });
  const { setError, formState } = form;
  const { errors } = formState;
  const [responseData, setResponseData] = useState<PointToPointTransportCostBreakdownResultDTO | null>(null);
  const { isPending, mutate: createTransportPrices } = useApiTransportPricesV1EstimateCreate({
    mutation: {
      onError(error) {
        const errorMessages: string[] = [];
        if (isFieldErrorResponse(error)) {
          Object.keys(error.response?.data).forEach((field) => {
            setError(field as keyof FormValues, { message: error.response.data[field] });
            errorMessages.push(error.response.data[field]);
          });
        }
        setError('root', { message: errorMessages.join(', ') || t('transportCostsEstimator.errors.anErrorOccurred') });

        setResponseData(null);
      },
      async onSuccess(data) {
        setResponseData(data);
      },
    },
  });

  const onSubmit = async (values: FormValues) => {
    createTransportPrices({
      data: {
        container_type: values.container_type,
        currency: values.currency,
        demand_incoterm: values.demand_incoterm,
        destination: values.destination
          ? {
              country: (values.destination.country_code as Country) || '',
              full_address: values.destination.full_address || '',
              lat: values.destination.latitude || 0,
              lon: values.destination.longitude || 0,
            }
          : undefined,
        nearest_city_max_radius: values.nearest_city_max_radius.toString(),
        origin: values.origin
          ? {
              country: (values.origin.country_code as Country) || '',
              full_address: values.origin.full_address || '',
              lat: values.origin.latitude || 0,
              lon: values.origin.longitude || 0,
            }
          : undefined,
        port_of_discharge: values.port_of_discharge || undefined,
        port_of_loading: values.port_of_loading || undefined,
        supply_incoterm: values.supply_incoterm,
      },
    });
  };
  return (
    <Page>
      <Page.Title title={t('transportCostsEstimator.pageTitle')} />
      <Form {...form} onSubmit={onSubmit}>
        {errors.root ? <Alert severity="error">{String(errors.root.message)}</Alert> : null}
        <Grid container spacing={2}>
          <Grid
            size={{
              md: 6,
              xs: 12,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                my: 1,
              }}
            >
              {t('transportCostsEstimator.sections.supply')}
            </Typography>
            <Grid container spacing={2} sx={{ flexDirection: 'column' }}>
              <Grid size={12}>
                <AddressAutocomplete name="origin" label={t('transportCostsEstimator.labels.originAddress')} />
              </Grid>
              <Grid size={12}>
                <PortAutocomplete
                  name="port_of_loading"
                  label={t('transportCostsEstimator.labels.portOfLoading')}
                  type={PortTypes.REG}
                />
              </Grid>
              <Grid size={12}>
                <IncotermSelectField
                  name="supply_incoterm"
                  label={t('transportCostsEstimator.labels.incoterm')}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid
            size={{
              md: 6,
              xs: 12,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                my: 1,
              }}
            >
              {t('transportCostsEstimator.sections.demand')}
            </Typography>
            <Grid container spacing={2} sx={{ flexDirection: 'column' }}>
              <Grid size={12}>
                <AddressAutocomplete
                  name="destination"
                  label={t('transportCostsEstimator.labels.destinationAddress')}
                />
              </Grid>
              <Grid size={12}>
                <PortAutocomplete
                  name="port_of_discharge"
                  label={t('transportCostsEstimator.labels.portOfDischarge')}
                  type={PortTypes.REG}
                />
              </Grid>
              <Grid size={12}>
                <IncotermSelectField
                  name="demand_incoterm"
                  label={t('transportCostsEstimator.labels.incoterm')}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid size={12}>
            <Typography
              variant="h6"
              sx={{
                my: 1,
              }}
            >
              {t('transportCostsEstimator.sections.extraOptions')}
            </Typography>
          </Grid>
          <Grid
            size={{
              md: 4,
              xs: 12,
            }}
          >
            <TextField
              name="nearest_city_max_radius"
              label={t('transportCostsEstimator.labels.nearestCityMaxRadius')}
              type="number"
              fullWidth
            />
          </Grid>
          <Grid
            size={{
              md: 4,
              xs: 12,
            }}
          >
            <SelectField
              label={t('transportCostsEstimator.labels.currency')}
              name="currency"
              options={CURRENCY_OPTIONS}
            />
          </Grid>
          <Grid
            size={{
              md: 4,
              xs: 12,
            }}
          >
            <SelectField
              label={t('transportCostsEstimator.labels.containerType')}
              name="container_type"
              options={CONTAINER_TYPE_OPTIONS}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'end',
            paddingTop: 2,
          }}
        >
          <Button type="submit" size="large" loading={isPending}>
            {t('transportCostsEstimator.actions.estimate')}
          </Button>
        </Box>
      </Form>
      <Divider sx={{ my: 2 }} />
      {responseData ? (
        <Stack spacing={2}>
          <Typography variant="h5" fontWeight="bold">
            {t('transportCostsEstimator.sections.transportPrices')}
          </Typography>

          <Grid container sx={{ justifyContent: 'start' }}>
            <Grid
              size={{
                md: 10,
                xs: 12,
              }}
            >
              <Table>
                <TableBody>
                  {responseData.missing_costs && responseData.missing_costs.length > 0 ? (
                    <>
                      <SeparatorRow title={t('transportCostsEstimator.separatorRows.missingCosts')} />
                      {responseData.missing_costs.map((cost) => (
                        <TableRow key={cost}>
                          <TableCell sx={{ bgcolor: 'orange' }}>
                            <Typography>{displayMissingCost(cost)}</Typography>
                          </TableCell>
                          <TableCell />
                          <TableCell />
                        </TableRow>
                      ))}
                    </>
                  ) : null}
                  {responseData.is_from_expired_quote ? (
                    <>
                      <SeparatorRow title={t('transportCostsEstimator.separatorRows.otherWarnings')} />
                      <TableRow>
                        <TableCell sx={{ bgcolor: 'orange' }}>
                          <Typography>{t('transportCostsEstimator.warnings.expiredQuote')}</Typography>
                        </TableCell>
                        <TableCell />
                        <TableCell />
                      </TableRow>
                    </>
                  ) : null}
                  {responseData.local_origin_costs && responseData.local_origin_costs.length > 0 ? (
                    <>
                      <SeparatorRow title={t('transportCostsEstimator.separatorRows.localOriginCosts')} />
                      {responseData.local_origin_costs.map((cost) => (
                        <TableRow key={cost.category}>
                          <TableCell>
                            <Typography fontWeight="bold">{displayLocalCostCategory(cost.category)}:</Typography>
                          </TableCell>
                          <TableCell sx={{ bgcolor: cost.is_included_in_total ? 'lightgreen' : 'lightsalmon' }}>
                            <Typography>
                              {displayCostInTargetAndOriginalCurrency(
                                cost.cost,
                                cost.currency,
                                cost.cost_in_target_currency,
                                responseData.target_currency
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography>{displayTransportCostSource(cost.source)}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  ) : null}
                  {responseData.origin_road_cost && responseData.origin_road_cost.cost_in_target_currency ? (
                    <>
                      <SeparatorRow title={t('transportCostsEstimator.separatorRows.originRoad')} />

                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="bold">{t('transportCostsEstimator.fields.cost')}</Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            bgcolor: responseData.origin_road_cost.is_included_in_total ? 'lightgreen' : 'lightsalmon',
                          }}
                        >
                          <Typography>
                            {displayCostInTargetAndOriginalCurrency(
                              responseData.origin_road_cost.cost,
                              responseData.origin_road_cost.currency,
                              responseData.origin_road_cost.cost_in_target_currency,
                              responseData.target_currency
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{displayTransportCostSource(responseData.origin_road_cost.source)}</Typography>
                        </TableCell>
                      </TableRow>
                    </>
                  ) : null}
                  {responseData.origin_road_cost?.nearest_city ? (
                    <TableRow>
                      <TableCell>
                        <Typography fontWeight="bold">
                          {t('transportCostsEstimator.fields.originNearestCity')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>{responseData.origin_road_cost.nearest_city}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>
                          {t('transportCostsEstimator.values.kmAway', {
                            values: { distance: responseData.origin_road_cost.nearest_city_distance },
                          })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {responseData.origin_terminal_handling_cost !== null ||
                  responseData.ocean_freight_cost ||
                  responseData.destination_terminal_handling_cost ? (
                    <SeparatorRow title={t('transportCostsEstimator.separatorRows.oceanFreightAndPortCharges')} />
                  ) : null}

                  {responseData.used_port_of_loading ? (
                    <TableRow>
                      <TableCell>
                        <Typography fontWeight="bold">{t('transportCostsEstimator.fields.originPort')}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>
                          {responseData.used_port_of_loading.name} ({responseData.used_port_of_loading.unlocode})
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {responseData.origin_terminal_handling_cost !== null &&
                    responseData.origin_terminal_handling_cost.cost_in_target_currency !== null && (
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="bold">{t('transportCostsEstimator.fields.othc')}</Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            bgcolor: responseData.origin_terminal_handling_cost.is_included_in_total
                              ? 'lightgreen'
                              : 'lightsalmon',
                          }}
                        >
                          <Typography>
                            {displayCostInTargetAndOriginalCurrency(
                              responseData.origin_terminal_handling_cost.cost,
                              responseData.origin_terminal_handling_cost.currency,
                              responseData.origin_terminal_handling_cost.cost_in_target_currency,
                              responseData.target_currency
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {displayTransportCostSource(responseData.origin_terminal_handling_cost.source)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}

                  {responseData.ocean_freight_cost ? (
                    <>
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="bold">
                            {t('transportCostsEstimator.fields.isOthcIncludedInOceanFreightRate')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {getIncludedDisplayedName(
                              responseData.ocean_freight_cost.is_origin_terminal_handling_cost_included,
                              t
                            )}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="bold">
                            {t('transportCostsEstimator.fields.oceanFreightCost')}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            bgcolor: responseData.ocean_freight_cost.is_included_in_total
                              ? 'lightgreen'
                              : 'lightsalmon',
                          }}
                        >
                          <Typography>
                            {displayCostInTargetAndOriginalCurrency(
                              responseData.ocean_freight_cost.cost,
                              responseData.ocean_freight_cost.currency,
                              responseData.ocean_freight_cost.cost_in_target_currency,
                              responseData.target_currency
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{displayTransportCostSource(responseData.ocean_freight_cost.source)}</Typography>
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="bold">
                            {t('transportCostsEstimator.fields.oceanFreightQuotedAt')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{formatDate(responseData.ocean_freight_cost.quoted_on)}</Typography>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </>
                  ) : null}

                  {responseData.used_port_of_discharge ? (
                    <TableRow>
                      <TableCell>
                        <Typography fontWeight="bold">{t('transportCostsEstimator.fields.destinationPort')}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>
                          {responseData.used_port_of_discharge.name} ({responseData.used_port_of_discharge.unlocode})
                        </Typography>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  ) : null}

                  {responseData.destination_terminal_handling_cost !== null &&
                    responseData.destination_terminal_handling_cost.cost_in_target_currency !== null && (
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="bold">{t('transportCostsEstimator.fields.dthc')}</Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            bgcolor: responseData.destination_terminal_handling_cost.is_included_in_total
                              ? 'lightgreen'
                              : 'lightsalmon',
                          }}
                        >
                          <Typography>
                            {displayCostInTargetAndOriginalCurrency(
                              responseData.destination_terminal_handling_cost.cost,
                              responseData.destination_terminal_handling_cost.currency,
                              responseData.destination_terminal_handling_cost.cost_in_target_currency,
                              responseData.target_currency
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {displayTransportCostSource(responseData.destination_terminal_handling_cost.source)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}

                  {responseData.ocean_freight_cost ? (
                    <TableRow>
                      <TableCell>
                        <Typography fontWeight="bold">
                          {t('transportCostsEstimator.fields.isDthcIncludedInOceanFreightRate')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>
                          {getIncludedDisplayedName(
                            responseData.ocean_freight_cost.is_destination_terminal_handling_cost_included,
                            t
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  ) : null}

                  {responseData.destination_road_cost && responseData.destination_road_cost.cost_in_target_currency ? (
                    <>
                      <SeparatorRow title={t('transportCostsEstimator.separatorRows.destinationRoad')} />
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="bold">{t('transportCostsEstimator.fields.cost')}</Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            bgcolor: responseData.destination_road_cost.is_included_in_total
                              ? 'lightgreen'
                              : 'lightsalmon',
                          }}
                        >
                          <Typography>
                            {displayCostInTargetAndOriginalCurrency(
                              responseData.destination_road_cost.cost,
                              responseData.destination_road_cost.currency,
                              responseData.destination_road_cost.cost_in_target_currency,
                              responseData.target_currency
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {displayTransportCostSource(responseData.destination_road_cost.source)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </>
                  ) : null}
                  {responseData.destination_road_cost?.nearest_city ? (
                    <TableRow>
                      <TableCell>
                        <Typography fontWeight="bold">{t('transportCostsEstimator.fields.nearestCity')}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>{responseData.destination_road_cost.nearest_city}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>
                          {t('transportCostsEstimator.values.kmAway', {
                            values: { distance: responseData.destination_road_cost.nearest_city_distance },
                          })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {responseData.direct_land_cost ? (
                    <>
                      <SeparatorRow title={t('transportCostsEstimator.separatorRows.directLandTransport')} />
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="bold">{t('transportCostsEstimator.fields.cost')}</Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            bgcolor: responseData.direct_land_cost.is_included_in_total ? 'lightgreen' : 'lightsalmon',
                          }}
                        >
                          <Typography>
                            {displayCostInTargetAndOriginalCurrency(
                              responseData.direct_land_cost.cost,
                              responseData.direct_land_cost.currency,
                              responseData.direct_land_cost.cost_in_target_currency,
                              responseData.target_currency
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{displayTransportCostSource(responseData.direct_land_cost.source)}</Typography>
                        </TableCell>
                      </TableRow>
                    </>
                  ) : null}
                  {responseData.local_destination_costs && responseData.local_destination_costs.length > 0 ? (
                    <>
                      <SeparatorRow title={t('transportCostsEstimator.separatorRows.localDestinationCosts')} />
                      {responseData.local_destination_costs.map((cost) => (
                        <TableRow key={cost.category}>
                          <TableCell>
                            <Typography fontWeight="bold">{displayLocalCostCategory(cost.category)}:</Typography>
                          </TableCell>
                          <TableCell sx={{ bgcolor: cost.is_included_in_total ? 'lightgreen' : 'lightsalmon' }}>
                            <Typography>
                              {displayCostInTargetAndOriginalCurrency(
                                cost.cost,
                                cost.currency,
                                cost.cost_in_target_currency,
                                responseData.target_currency
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography>{displayTransportCostSource(cost.source)}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  ) : null}
                  {responseData.e2e_cost && responseData.e2e_cost.cost_in_target_currency ? (
                    <>
                      <SeparatorRow title={t('transportCostsEstimator.separatorRows.e2eQuoteSharpQuote')} />
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="bold">{t('transportCostsEstimator.fields.cost')}</Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            bgcolor: responseData.e2e_cost.is_included_in_total ? 'lightgreen' : 'lightsalmon',
                          }}
                        >
                          <Typography>
                            {displayCostInTargetAndOriginalCurrency(
                              responseData.e2e_cost.cost,
                              responseData.e2e_cost.currency,
                              responseData.e2e_cost.cost_in_target_currency,
                              responseData.target_currency
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>{displayTransportCostSource(responseData.e2e_cost.source)}</Typography>
                        </TableCell>
                      </TableRow>
                    </>
                  ) : null}
                  <SeparatorRow title={t('transportCostsEstimator.separatorRows.total')} />
                  <TableRow>
                    <TableCell>
                      <Typography
                        fontWeight="bold"
                        sx={{
                          fontSize: 24,
                        }}
                      >
                        {t('transportCostsEstimator.fields.totalCost')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        fontWeight="bold"
                        sx={{
                          fontSize: 24,
                        }}
                      >
                        {formatCurrency(responseData.total_cost_in_target_currency, {
                          currencyCode: responseData.target_currency,
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </Grid>
          </Grid>
        </Stack>
      ) : null}
    </Page>
  );
};
